// MoveRiskDialog — move a risk to a different register.
// Fetches the full register tree and presents it via HierarchyPickerDialog.
// Shows a toast (via onMoved callback) when parent_severed is true.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { risksApi } from '@/lib/api/risks'
import { registersApi, type Register } from '@/lib/api/registers'
import { queryKeys } from '@/lib/api/queryKeys'
import { HierarchyPickerDialog, type HierarchyNode } from '@/components/shared/HierarchyPickerDialog'

interface MoveRiskDialogProps {
  open: boolean
  onClose: () => void
  onMoved: (parentSevered: boolean) => void
  riskId: string
  currentRegisterId: string
}

export function MoveRiskDialog({
  open,
  onClose,
  onMoved,
  riskId,
  currentRegisterId,
}: MoveRiskDialogProps) {
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()
  const rApi = risksApi(api)
  const regApi = registersApi(api)

  const { data: registers } = useQuery({
    queryKey: queryKeys.registers.tree(slug),
    queryFn: () => regApi.tree(),
    enabled: open,
  })

  const moveMutation = useMutation({
    mutationFn: ({ destId, notes }: { destId: string; notes: string }) =>
      rApi.move(riskId, destId, notes),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.risks.detail(slug, riskId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.registers.tree(slug) })
      queryClient.invalidateQueries({ queryKey: queryKeys.risks.all(slug) })
      onMoved(result.parent_severed)
    },
  })

  const nodes: HierarchyNode[] = (registers ?? []).map((r: Register) => ({
    id: r.id,
    name: r.name,
    parent_id: r.parent_id,
  }))

  const currentRegisterName = registers?.find((r) => r.id === currentRegisterId)?.name

  function handleConfirm(selectedId: string, changeNotes: string) {
    moveMutation.mutate({ destId: selectedId, notes: changeNotes })
  }

  return (
    <HierarchyPickerDialog
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      title="Move Risk"
      subtitle={currentRegisterName ? `Current register: ${currentRegisterName}` : undefined}
      nodeLabel="register"
      nodes={nodes}
      currentId={currentRegisterId}
      isPending={moveMutation.isPending}
      error={moveMutation.error ? (moveMutation.error as Error).message : null}
    />
  )
}
